import { useState, useEffect, useCallback } from "react";
import { supabase, isSupabaseConfigured } from "./supabaseClient";
import staticAnalyses, { mediaGuideData as staticMediaGuide } from "./data/analyses";

// Normalize field names (Supabase uses snake_case, old code used camelCase)
function normalizeAnalysis(a) {
  return {
    ...a,
    sourceColor: a.source_color || a.sourceColor,
    readTime: a.read_time || a.readTime,
    factTags: a.fact_tags || a.factTags,
    pullQuote: a.pull_quote || a.pullQuote,
    apComparison: a.ap_comparison || a.apComparison,
  };
}

function normalizeOutlet(o) {
  return {
    ...o,
    whyCritique: o.why_critique || o.whyCritique,
  };
}

// Everything except `decoded` (the full article body) and the unused
// `ap_comparison`. The bodies are ~90% of the table's bytes; downloading all
// 300+ of them on every page load was burning through the Supabase egress
// quota. Card previews use pull_quote; the body is fetched per-article on
// open via useAnalysisBody below.
const LIST_COLUMNS =
  "id,number,source,source_color,category,headline,date,read_time,fact_tags,pull_quote,upvotes,downvotes,is_published,is_premium,created_at,updated_at";

// ---- Offline cache (persists across restarts; required for the iOS app to
// show content without a connection). All storage access is wrapped so a
// blocked/full localStorage can never crash the app.
const CACHE_LIST_KEY = "btn_cache_list";
const CACHE_BODIES_KEY = "btn_cache_bodies";
const CACHE_GUIDE_KEY = "btn_cache_guide";
const CACHE_LIST_MAX = 150; // newest list rows kept for offline (~60KB)
const CACHE_BODIES_MAX = 80; // article bodies kept for offline (~320KB)
const PREFETCH_BODIES = 20; // newest bodies fetched ahead so fresh pieces read offline

function readCache(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (_) {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
}

function readBodyStore() {
  const v = readCache(CACHE_BODIES_KEY);
  return Array.isArray(v) ? v : [];
}

function readBodyFromStore(id) {
  const entry = readBodyStore().find((e) => e.id === id);
  return entry ? entry.decoded : null;
}

function saveBodyToStore(id, decoded) {
  const store = readBodyStore().filter((e) => e.id !== id);
  store.unshift({ id, decoded, t: Date.now() });
  writeCache(CACHE_BODIES_KEY, store.slice(0, CACHE_BODIES_MAX));
}

async function prefetchLatestBodies(ids) {
  try {
    const missing = ids.filter((id) => !bodyCache.has(id) && readBodyFromStore(id) == null);
    if (missing.length === 0) return;
    const { data } = await supabase.from("analyses").select("id,decoded").in("id", missing);
    if (data) {
      for (const row of data) {
        bodyCache.set(row.id, row.decoded || "");
        saveBodyToStore(row.id, row.decoded || "");
      }
    }
  } catch (_) {}
}

export function useAnalyses() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingSupabase, setUsingSupabase] = useState(false);
  const [offline, setOffline] = useState(false);

  const fetchAnalyses = useCallback(async () => {
    if (isSupabaseConfigured()) {
      try {
        const { data, error } = await supabase
          .from("analyses")
          .select(LIST_COLUMNS)
          .eq("is_published", true)
          .order("created_at", { ascending: false });

        if (!error && data && data.length > 0) {
          // Attach per-analysis comment counts so cards show real 💬 numbers
          const counts = {};
          try {
            // PostgREST caps responses at 1000 rows, so page through the
            // comments table or every piece past the first ~1000 comments
            // silently shows 💬 0 on its card.
            const PAGE = 1000;
            for (let from = 0; ; from += PAGE) {
              const { data: commentRows } = await supabase
                .from("comments")
                .select("analysis_id")
                .range(from, from + PAGE - 1);
              if (!commentRows || commentRows.length === 0) break;
              for (const c of commentRows) {
                if (c.analysis_id != null) {
                  counts[c.analysis_id] = (counts[c.analysis_id] || 0) + 1;
                }
              }
              if (commentRows.length < PAGE) break;
            }
          } catch (e) {
            // counts stay empty — cards just show 0
          }
          const normalized = data.map((a) => ({
            ...normalizeAnalysis(a),
            comments: counts[a.id] || 0,
          }));
          setAnalyses(normalized);
          setUsingSupabase(true);
          setOffline(false);
          setLoading(false);
          writeCache(CACHE_LIST_KEY, normalized.slice(0, CACHE_LIST_MAX));
          prefetchLatestBodies(normalized.slice(0, PREFETCH_BODIES).map((a) => a.id));
          return;
        }
      } catch (e) {
        // Fall through to offline cache / static data
      }
    }
    // Offline: serve the last successfully fetched list if we have one
    const cachedList = readCache(CACHE_LIST_KEY);
    if (Array.isArray(cachedList) && cachedList.length > 0) {
      setAnalyses(cachedList);
      setUsingSupabase(false);
      setOffline(true);
      setLoading(false);
      return;
    }
    // Fallback to static data
    setAnalyses(staticAnalyses.map(normalizeAnalysis));
    setUsingSupabase(false);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAnalyses();
  }, [fetchAnalyses]);

  return { analyses, loading, usingSupabase, offline, refetch: fetchAnalyses };
}

// Fetches the full body (`decoded`) of ONE analysis on demand, with a module-level
// cache so re-opening a piece doesn't refetch. Static-data posts (and any row that
// already carries decoded, e.g. from the Explore static fallback) short-circuit.
const bodyCache = new Map(); // id -> decoded string

export function useAnalysisBody(post) {
  const id = post ? post.id : null;
  const inlineBody = post && typeof post.decoded === "string" ? post.decoded : null;
  const [body, setBody] = useState(
    inlineBody != null ? inlineBody : id != null && bodyCache.has(id) ? bodyCache.get(id) : ""
  );

  useEffect(() => {
    let cancelled = false;
    if (inlineBody != null || id == null) return undefined;
    if (bodyCache.has(id)) {
      setBody(bodyCache.get(id));
      return undefined;
    }
    // Serve the persisted offline copy immediately (if any), then still
    // refresh from the network so post-publish corrections come through.
    const stored = readBodyFromStore(id);
    if (stored != null) {
      bodyCache.set(id, stored);
      setBody(stored);
    }
    if (!isSupabaseConfigured()) return undefined;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("analyses")
          .select("decoded")
          .eq("id", id)
          .single();
        if (!cancelled && !error && data) {
          bodyCache.set(id, data.decoded || "");
          saveBodyToStore(id, data.decoded || "");
          setBody(data.decoded || "");
        }
      } catch (_) {
        // Offline — the stored copy (if any) stays on screen
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, inlineBody]);

  return inlineBody != null ? inlineBody : body;
}

export function useMediaGuide() {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      if (isSupabaseConfigured()) {
        try {
          const { data, error } = await supabase
            .from("media_guide")
            .select("*")
            .order("tier", { ascending: true });

          if (!error && data && data.length > 0) {
            const normalized = data.map(normalizeOutlet);
            setOutlets(normalized);
            setLoading(false);
            writeCache(CACHE_GUIDE_KEY, normalized);
            return;
          }
        } catch (e) {
          // Fall through
        }
      }
      const cachedGuide = readCache(CACHE_GUIDE_KEY);
      if (Array.isArray(cachedGuide) && cachedGuide.length > 0) {
        setOutlets(cachedGuide);
        setLoading(false);
        return;
      }
      setOutlets(staticMediaGuide.map(normalizeOutlet));
      setLoading(false);
    }
    fetch();
  }, []);

  return { outlets, loading };
}

const BLOCKED_AUTHORS_KEY = "btn_blocked_authors";
const REPORTED_COMMENTS_KEY = "btn_reported_comments";

function readStoredList(key) {
  try {
    const v = JSON.parse(localStorage.getItem(key));
    return Array.isArray(v) ? v : [];
  } catch (_) {
    return [];
  }
}

function writeStoredList(key, list) {
  try {
    localStorage.setItem(key, JSON.stringify(list));
  } catch (_) {}
}

export function useComments(analysisId) {
  const [comments, setComments] = useState([]);
  const [blockedAuthors, setBlockedAuthors] = useState(() => readStoredList(BLOCKED_AUTHORS_KEY));
  const [reportedIds, setReportedIds] = useState(() => readStoredList(REPORTED_COMMENTS_KEY));

  useEffect(() => {
    async function fetch() {
      if (!isSupabaseConfigured() || !analysisId) {
        setComments([
          { author: "MediaWatcher", text: "This is exactly the kind of analysis we need. Well sourced.", created_at: new Date(Date.now() - 7200000).toISOString() },
          { author: "CriticalThinker22", text: "Checked the AP source \u2014 the framing difference is wild.", created_at: new Date(Date.now() - 14400000).toISOString() },
        ]);
        return;
      }
      const { data } = await supabase
        .from("comments")
        .select("*")
        .eq("analysis_id", analysisId)
        .order("created_at", { ascending: false });
      if (data) setComments(data);
    }
    fetch();
  }, [analysisId]);

  const addComment = async (text, author = "Anonymous") => {
    const newComment = {
      analysis_id: analysisId,
      author,
      text,
      created_at: new Date().toISOString(),
    };
    if (isSupabaseConfigured()) {
      const { data } = await supabase.from("comments").insert(newComment).select();
      if (data) {
        setComments((prev) => [data[0], ...prev]);
        return;
      }
    }
    setComments((prev) => [{ ...newComment, id: Date.now() }, ...prev]);
  };

  const reportComment = async (comment) => {
    if (!comment) return;
    if (comment.id != null) {
      const next = [...new Set([...reportedIds, comment.id])];
      setReportedIds(next);
      writeStoredList(REPORTED_COMMENTS_KEY, next);
      if (isSupabaseConfigured()) {
        await supabase.from("comment_reports").insert({ comment_id: comment.id });
      }
    }
  };

  const blockAuthor = (author) => {
    if (!author) return;
    const next = [...new Set([...blockedAuthors, author])];
    setBlockedAuthors(next);
    writeStoredList(BLOCKED_AUTHORS_KEY, next);
  };

  const unblockAllAuthors = () => {
    setBlockedAuthors([]);
    writeStoredList(BLOCKED_AUTHORS_KEY, []);
  };

  const visibleComments = comments.filter(
    (c) => !blockedAuthors.includes(c.author) && (c.id == null || !reportedIds.includes(c.id))
  );

  return {
    comments: visibleComments,
    addComment,
    reportComment,
    blockAuthor,
    unblockAllAuthors,
    blockedCount: blockedAuthors.length,
  };
}

// Community page data: recent comments across all analyses + totals,
// and reader headline suggestions (stored as comments with analysis_id = null).
export function useCommunityData() {
  const [recentComments, setRecentComments] = useState([]);
  const [commentCount, setCommentCount] = useState(0);

  useEffect(() => {
    async function fetch() {
      if (!isSupabaseConfigured()) return;
      try {
        const { data } = await supabase
          .from("comments")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(15);
        if (data) setRecentComments(data);
        const { count } = await supabase
          .from("comments")
          .select("*", { count: "exact", head: true });
        if (count != null) setCommentCount(count);
      } catch (e) {
        // Leave defaults — the page still renders stats from analyses
      }
    }
    fetch();
  }, []);

  const addSuggestion = async (text) => {
    const trimmed = (text || "").trim();
    if (!isSupabaseConfigured() || !trimmed) return false;
    const { data, error } = await supabase
      .from("comments")
      .insert({ analysis_id: null, author: "Reader suggestion", text: trimmed })
      .select();
    if (error) return false;
    if (data && data[0]) setRecentComments((prev) => [data[0], ...prev]);
    setCommentCount((c) => c + 1);
    return true;
  };

  return { recentComments, commentCount, addSuggestion };
}

export function useVote(analysisId, initialUp, initialDown) {
  const [votes, setVotes] = useState({ up: initialUp, down: initialDown, voted: null });

  const handleVote = async (type) => {
    setVotes((prev) => {
      if (prev.voted === type) {
        return {
          up: type === "up" ? prev.up - 1 : prev.up,
          down: type === "down" ? prev.down - 1 : prev.down,
          voted: null,
        };
      }
      return {
        up: type === "up" ? prev.up + 1 : prev.voted === "up" ? prev.up - 1 : prev.up,
        down: type === "down" ? prev.down + 1 : prev.voted === "down" ? prev.down - 1 : prev.down,
        voted: type,
      };
    });
  };

  return { votes, handleVote };
}

// Time formatting helper
export function timeAgo(dateString) {
  if (!dateString) return "";
  const now = new Date();
  const date = new Date(dateString);
  if (isNaN(date)) return "";
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
